import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { API, axios } from "@/api";
import {
  ArrowLeft, ExternalLink, Shield, Clock, Star, CheckCircle,
  AlertTriangle, ThumbsUp, ThumbsDown, Flag, Loader2,
} from "lucide-react";
import { toast } from "sonner";

const STALENESS = {
  fresh: { color: "bg-emerald-500", text: "text-emerald-600", label: "" },
  recent: { color: "bg-yellow-400", text: "text-yellow-600", label: (d) => `Verified ${d} days ago` },
  stale: { color: "bg-amber-500", text: "text-amber-600", label: () => "Offer details may have changed — confirm before visiting" },
  unknown: { color: "bg-slate-400", text: "text-slate-500", label: () => "Not yet community verified" },
};

export default function OfferDetailPage() {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReport, setShowReport] = useState(false);
  const [reportText, setReportText] = useState("");
  const [showClaimResult, setShowClaimResult] = useState(false);
  const [claimNotes, setClaimNotes] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/birthday-offers/${offerId}`);
        setOffer(res.data);
      } catch { setOffer(null); }
      finally { setLoading(false); }
    })();
  }, [offerId]);

  const goBack = () => {
    if (location.state?.from) {
      navigate(location.state.from, { state: location.state?.parentState });
    } else {
      navigate(-1);
    }
  };

  const handleClaimResult = async (success) => {
    try {
      const token = localStorage.getItem("token") || localStorage.getItem("access_token");
      await axios.post(`${API}/birthday-offers/${offerId}/claim-result`, { success, notes: claimNotes }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(success ? "Thanks! Glad it worked!" : "Thanks for reporting — this helps others.");
      setShowClaimResult(false);
      setClaimNotes("");
      const res = await axios.get(`${API}/birthday-offers/${offerId}`);
      setOffer(res.data);
    } catch {
      toast.error("Could not submit — please try again");
    }
  };

  if (loading) return <div className="flex justify-center items-center min-h-screen"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>;
  if (!offer) return <div className="flex flex-col items-center justify-center min-h-screen px-4"><p className="text-slate-500">Offer not found</p><button onClick={goBack} className="mt-4 text-amber-600 font-semibold">Go Back</button></div>;

  const biz = offer.business || {};
  const staleness = STALENESS[offer.staleness_level] || STALENESS.unknown;
  const showRate = offer.redemption_attempt_count >= 5 && offer.redemption_success_rate != null;
  const ratePercent = showRate ? Math.round(offer.redemption_success_rate * 100) : null;

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="offer-detail-page">
      <div className="sticky top-0 z-20 bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
        <button onClick={goBack} className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center" data-testid="offer-back-btn">
          <ArrowLeft className="w-4 h-4 text-slate-600" />
        </button>
        <p className="text-sm font-semibold text-slate-800 truncate flex-1">Offer Details</p>
      </div>

      <div className="px-4 py-4 space-y-5 max-w-2xl mx-auto">
        {/* Business Summary */}
        <button onClick={() => navigate(`/business/${biz.business_id}`)} className="w-full text-left bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-3" data-testid="offer-business-link">
          {biz.logo_url && <img src={biz.logo_url} alt="" className="w-10 h-10 rounded-lg object-contain bg-slate-50" />}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-slate-800 truncate">{biz.name}</p>
            <p className="text-xs text-slate-500">{biz.category} — {biz.city}, {biz.state}</p>
          </div>
          <ExternalLink className="w-4 h-4 text-slate-400 flex-shrink-0" />
        </button>

        {/* Title + Badges */}
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="offer-title-section">
          <div className="flex items-start gap-2 flex-wrap mb-2">
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700">{offer.offer_type?.replace(/_/g, " ")}</span>
            {offer.is_true_freebie && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700">True Freebie</span>}
            {offer.loyalty_program_required && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-violet-100 text-violet-700">Loyalty Required</span>}
            <span className={`w-2 h-2 rounded-full ${staleness.color} mt-1`} />
          </div>
          <h1 className="text-xl font-black text-slate-900">{offer.offer_title}</h1>
          {staleness.label && typeof staleness.label === "function" && (
            <p className={`text-xs mt-1 ${staleness.text}`}>{staleness.label(30)}</p>
          )}
        </div>

        {/* Plain Language Summary */}
        {offer.offer_plain_language_summary && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="offer-summary-section">
            <p className="text-sm text-slate-800 leading-relaxed">{offer.offer_plain_language_summary}</p>
          </div>
        )}

        {/* What You Get */}
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="offer-what-you-get">
          <h2 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">What You Get</h2>
          <p className="text-sm text-slate-800">{offer.offer_title}</p>
          {offer.offer_value_est > 0 && <p className="text-xs text-slate-500 mt-1">Estimated value: ${offer.offer_value_est.toFixed(2)}</p>}
          {offer.min_spend > 0 && <p className="text-xs text-amber-600 mt-1">Minimum purchase: ${offer.min_spend.toFixed(2)} required</p>}
        </div>

        {/* How to Claim */}
        {offer.redemption_steps?.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="offer-redemption-steps">
            <h2 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">How to Claim</h2>
            <ol className="space-y-2">
              {offer.redemption_steps.map((step, i) => (
                <li key={i} className="flex gap-2">
                  <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</span>
                  <span className="text-sm text-slate-700 pt-0.5">{step}</span>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Loyalty Program */}
        {offer.loyalty_program_required && (
          <div className="bg-violet-50 border border-violet-200 rounded-xl p-4" data-testid="offer-loyalty-section">
            <h2 className="text-xs font-bold text-violet-600 uppercase tracking-wider mb-2">Loyalty Program Required</h2>
            {offer.loyalty_program_name && <p className="text-sm font-semibold text-slate-800">{offer.loyalty_program_name}</p>}
            {offer.loyalty_tier_required && <p className="text-xs text-violet-600 mt-1">Tier required: {offer.loyalty_tier_required}</p>}
            {offer.loyalty_program_explanation && <p className="text-sm text-slate-600 mt-2">{offer.loyalty_program_explanation}</p>}
            {offer.signup_url && (
              <a href={offer.signup_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-2 text-sm font-semibold text-violet-600">
                Sign up here <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        )}

        {/* Terms */}
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="offer-terms-section">
          <h2 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Terms</h2>
          <div className="space-y-1 text-sm text-slate-600">
            {offer.valid_days?.length > 0 && <p>Valid {offer.valid_days.join(", ")} only</p>}
            {offer.valid_hours && <p>Valid {offer.valid_hours.start} — {offer.valid_hours.end}</p>}
            {offer.min_spend > 0 && <p>Minimum ${offer.min_spend.toFixed(2)} purchase required</p>}
            {offer.available_months?.length > 0 && <p>Available in months: {offer.available_months.join(", ")}</p>}
            {offer.requires_signup && <p>Signup required{offer.signup_lead_days ? ` ${offer.signup_lead_days} days before birthday` : ""}</p>}
            <p>Redemption: {(offer.redemption_method || "").replace(/_/g, " ")}</p>
          </div>
        </div>

        {/* Success Rate */}
        {showRate && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center" data-testid="offer-success-rate">
            <p className="text-4xl font-black text-emerald-700">{ratePercent}%</p>
            <p className="text-sm text-emerald-600">of people successfully got this offer</p>
            <p className="text-xs text-slate-400 mt-1">Based on {offer.redemption_attempt_count} reports</p>
          </div>
        )}

        {/* Community Confirmations */}
        {offer.community_confirmation_count > 0 && (
          <div className="flex items-center gap-2 px-4 py-3 bg-sky-50 border border-sky-200 rounded-xl" data-testid="offer-confirmations">
            <CheckCircle className="w-5 h-5 text-sky-600" />
            <p className="text-sm text-sky-800">{offer.community_confirmation_count} {offer.community_confirmation_count === 1 ? "person" : "people"} confirmed this works</p>
          </div>
        )}

        {/* Source URL */}
        {offer.offer_source_url && (
          <a href={offer.offer_source_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-amber-600 font-semibold" data-testid="offer-source-link">
            <ExternalLink className="w-4 h-4" /> View original offer source
          </a>
        )}

        {/* Action Buttons */}
        <div className="space-y-2">
          <button onClick={() => setShowClaimResult(true)} className="w-full py-3 rounded-xl bg-amber-500 text-white font-bold text-sm hover:bg-amber-600 transition-colors" data-testid="offer-did-it-work-btn">
            <ThumbsUp className="w-4 h-4 inline mr-2" />Did this work for you?
          </button>
          <button onClick={() => setShowReport(true)} className="w-full py-2.5 rounded-xl border border-slate-300 text-slate-600 font-semibold text-sm hover:bg-slate-50" data-testid="offer-report-issue-btn">
            <Flag className="w-3.5 h-3.5 inline mr-1.5" />Report an issue
          </button>
        </div>
      </div>

      {/* Claim Result Modal */}
      {showClaimResult && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50" data-testid="claim-result-modal">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md p-5 space-y-4">
            <h3 className="text-base font-bold text-slate-800">Did this offer work?</h3>
            <textarea value={claimNotes} onChange={(e) => setClaimNotes(e.target.value)} placeholder="Any details? (optional)" className="w-full text-sm border border-slate-300 rounded-xl px-3 py-2 h-20 resize-none focus:outline-none focus:ring-1 focus:ring-amber-400" />
            <div className="flex gap-2">
              <button onClick={() => handleClaimResult(true)} className="flex-1 py-2.5 rounded-xl bg-emerald-600 text-white font-bold text-sm" data-testid="claim-yes-btn">
                <ThumbsUp className="w-4 h-4 inline mr-1" />Yes it worked
              </button>
              <button onClick={() => handleClaimResult(false)} className="flex-1 py-2.5 rounded-xl bg-red-500 text-white font-bold text-sm" data-testid="claim-no-btn">
                <ThumbsDown className="w-4 h-4 inline mr-1" />No it didn't
              </button>
            </div>
            <button onClick={() => setShowClaimResult(false)} className="w-full py-2 text-sm text-slate-500 font-semibold">Cancel</button>
          </div>
        </div>
      )}

      {/* Report Issue Modal */}
      {showReport && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50" data-testid="report-issue-modal">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md p-5 space-y-4">
            <h3 className="text-base font-bold text-slate-800">Report an issue</h3>
            <textarea value={reportText} onChange={(e) => setReportText(e.target.value)} placeholder="Describe the issue..." className="w-full text-sm border border-slate-300 rounded-xl px-3 py-2 h-24 resize-none focus:outline-none focus:ring-1 focus:ring-amber-400" />
            <button onClick={() => { toast.success("Report submitted — thank you!"); setShowReport(false); setReportText(""); }} className="w-full py-2.5 rounded-xl bg-slate-800 text-white font-bold text-sm" data-testid="submit-report-btn">Submit Report</button>
            <button onClick={() => setShowReport(false)} className="w-full py-2 text-sm text-slate-500 font-semibold">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
